import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import classnames from 'classnames';
import { useStoreActions, useStoreState } from '../store';
import { useLocale } from '../hooks/useLocale';
import checkIcon from '../assets/images/check-blue.svg';
import closeIcon from '../assets/images/close.svg';
import { theme } from '../utils/theme';
import { Browser } from '../utils/browser';

export const LanguageOptions = () => {
  const locale = useLocale();
  const controlsVisible = useStoreState(state => state.player.controlsVisible);
  const languageMenuVisible = useStoreState(state => state.player.languageMenuVisible);
  const subtitleOptionsVisible = useStoreState(state => state.player.subtitleOptionsVisible);
  const ignoreVideoEvents = useStoreState(state => state.player.ignoreVideoEvents);
  const audioTracks = useStoreState(state => state.player.audioTracks);
  const audioTrack = useStoreState(state => state.player.audioTrack);

  const webVttSubtitles = useStoreState(state => state.subtitles.webVttSubtitles);
  const activeSubtitle = useStoreState(state => state.subtitles.activeSubtitle);

  const actions = useStoreActions(actions => actions);
  const [visible, setVisible] = React.useState(false);
  const [transitioned, setTransitioned] = React.useState(false);

  function handleSubtitleOptionsClick() {
    actions.player.setSubtitleOptionsVisible(true);
  }

  function handleSubtitleChange(subtitle: TextTrack | null) {
    actions.subtitles.setActiveSubtitle(subtitle);
    actions.settings.setSubtitleTrack(subtitle);
  }

  function handleAudioTrackChange(track: AudioTrack) {
    if (ignoreVideoEvents) return;
    actions.player.setAudioTrack(track);
    actions.settings.setAudioTrack(track);
  }

  function handleClosePress() {
    actions.player.setLanguageMenuVisible(false);
  }

  function handleBackdropPress() {
    actions.player.setLanguageMenuVisible(false);
  }

  // Delay component unmount during animation
  React.useEffect(() => {
    const shouldDisplay = controlsVisible && languageMenuVisible && !subtitleOptionsVisible;
    setTransitioned(shouldDisplay);
    setTimeout(() => setVisible(shouldDisplay), shouldDisplay ? 0 : 250);
  }, [controlsVisible, languageMenuVisible, subtitleOptionsVisible]);

  // Display all WebVTT tracks
  const webVttSubtitleOptions = webVttSubtitles;

  return visible ? (
    <>
      <Backdrop onClick={handleBackdropPress} />
      <Container className={classnames(transitioned ? 'visible' : 'hidden')}>
        <Close onClick={handleClosePress} />
        <OptionsGroup>
          <OptionsGroupHeader>{locale.translations.LanguageOptions.Subtitles}</OptionsGroupHeader>
          <OptionsGroupItem
            active={activeSubtitle === null}
            onClick={() => handleSubtitleChange(null)}
            children={locale.translations.General.Off}
          />
          {webVttSubtitleOptions.map((webVttSubtitle: any) => (
            <OptionsGroupItem
              key={`webvtt-${webVttSubtitle.id}`}
              active={webVttSubtitle.label === activeSubtitle?.label}
              onClick={() => handleSubtitleChange(webVttSubtitle)}
              children={webVttSubtitle.label}
            />
          ))}
          {[...webVttSubtitles].length ? (
            <OptionsGroupButton onClick={handleSubtitleOptionsClick}>
              {locale.translations.LanguageOptions.SubtitleOptions}
            </OptionsGroupButton>
          ) : null}
        </OptionsGroup>
        <OptionsGroup>
          <OptionsGroupHeader>{locale.translations.LanguageOptions.Audio}</OptionsGroupHeader>
          {audioTracks
            .sort((trackA, trackB) => {
              if (Browser.isSafari || Browser.isIOS) return trackA.id > trackB.id ? 1 : -1;
              return 0;
            })
            .map(track => (
              <OptionsGroupItem
                key={track.id}
                active={audioTrack === track}
                disabled={ignoreVideoEvents}
                onClick={() => handleAudioTrackChange(track)}
                children={track.label}
              />
            ))}
        </OptionsGroup>
      </Container>
    </>
  ) : null;
};

const hiddenAnimation = keyframes`
  0%    { opacity: 1; }
  99%   { opacity: 0; }
  100%  { opacity: 0; }
`;

const visibleAnimation = keyframes`
  0%    { opacity: 0; }
  100%  { opacity: 1; }
`;

const Container = styled.div`
  background-color: rgba(22, 24, 25, 0.7);
  border: solid 1px #fff;
  border-radius: 4px;
  position: absolute;
  bottom: 165px;
  right: 32px;
  padding: 32px 48px 32px 12px;
  display: flex;
  overflow: auto;

  &.hidden {
    animation: ${hiddenAnimation} 0.3s;
  }

  &.visible {
    animation: ${visibleAnimation} 0.3s;
  }

  @media ${theme.mobileMediaQuery} {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ theme }) => theme.bbBlack};
    border: 0;
    flex-direction: column;
    padding: 0px;
    padding-top: 42px;
    z-index: 99999999;

    &.hidden {
      animation: none;
    }

    &.visible {
      animation: none;
    }
  }
`;

const Backdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const Close = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 30px;
  height: 30px;
  background: url(${closeIcon}) center / contain no-repeat;
  cursor: pointer;

  @media ${theme.mobileMediaQuery} {
    top: 24px;
    left: 21px;
    right: initial;
    width: 18px;
    height: 18px;
  }
`;

const OptionsGroup = styled.div`
  padding-left: 60px;

  @media ${theme.mobileMediaQuery} {
    padding-left: initial;
    padding: 32px 24px;
    border-bottom: 1px solid #fff;

    &:last-of-type {
      border-bottom: 0;
    }
  }
`;

const OptionsGroupHeader = styled.div`
  font-family: Lato;
  font-size: 18px;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: 0.4px;
  color: #f7f8fa;
  margin-top: 0px;
  margin-bottom: 8px;
  cursor: initial;
`;

const OptionsGroupItem = styled.div<{ active?: boolean; disabled?: boolean }>`
  position: relative;
  font-family: Lato;
  font-size: 14px;
  font-stretch: normal;
  font-style: normal;
  line-height: 24px;
  letter-spacing: 0.3px;
  color: ${theme.white};
  margin-top: 16px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: ${({ theme, disabled, active }) => (disabled && !active ? theme.white : theme.primaryBlue)};
    }
  }

  ${({ active }) =>
    active &&
    css`
      color: ${({ theme }) => theme.primaryBlue};

      &::before {
        content: '';
        position: absolute;
        width: 24px;
        height: 24px;
        background: url(${checkIcon}) center / contain no-repeat;
        left: -34px;

        @media ${theme.mobileMediaQuery} {
          left: calc(100% - 24px);
        }
      }
    `}
`;

const OptionsGroupButton = styled(OptionsGroupItem)`
  font-size: 12px;
  line-height: 1.5;
  letter-spacing: 0.3px;
  color: ${({ theme }) => theme.primaryBlue};
`;
